import React from 'react';
import {
  Box,
  Container,
  Heading,
  Text,
  VStack,
  Link,
  Divider,
  UnorderedList,
  ListItem
} from '@chakra-ui/react';
import { Link as RouterLink } from 'react-router-dom';
import Footer from './Footer';

const TermsOfService = () => (
  <Container maxW="container.md" py={8}>
    <VStack spacing={6} align="stretch">
      <Heading as="h1" size="xl">Terms of Service</Heading>
      <Text fontSize="sm" color="gray.600">Last Updated: April 3, 2026</Text>
      <Text fontSize="sm" color="gray.600">
        Operated by <strong>Setora, LLC</strong> on behalf of PlayMaker Pics clients
      </Text>
      <Divider />

      <Box>
        <Heading as="h2" size="lg" mb={4}>1. Introduction</Heading>
        <Text mb={3}>
          Welcome to PlayMaker Pics, a service operated by <strong>Setora, LLC</strong>. By using
          our service, you agree to these Terms of Service. Please read them carefully.
        </Text>
        <Text>
          Our services are designed to provide sports photography, personalized graphics, and image
          management for athletes, teams, events, parents, and guardians. These Terms govern your
          use of our website, applications, SMS messaging program, and any related services.
        </Text>
      </Box>

      <Box>
        <Heading as="h2" size="lg" mb={4}>2. SMS Messaging Program</Heading>

        <Heading as="h3" size="md" mb={2}>2.1 Program Name & Description</Heading>
        <Text mb={3}>
          <strong>Program Name:</strong> PlayMaker Pics Notifications
        </Text>
        <Text mb={3}>
          <strong>Program Description:</strong> PlayMaker Pics Notifications is an SMS messaging
          program that sends text messages to opted-in parents, guardians, and authorized contacts
          regarding their athlete's photos, order confirmations, delivery updates, event reminders,
          account alerts, and promotional offers related to their PlayMaker Pics account.
        </Text>

        <Heading as="h3" size="md" mb={2}>2.2 Message Frequency</Heading>
        <Text mb={3}>
          Message frequency varies based on your account activity, event participation, and the
          campaigns you are enrolled in. You can expect to receive approximately 1 to 10 messages
          per month. During active photo sessions or promotional periods, frequency may temporarily
          increase. Recurring messages will be sent for the duration of your opt-in.
        </Text>

        <Heading as="h3" size="md" mb={2}>2.3 Message and Data Rates</Heading>
        <Text mb={3}>
          <strong>Message and data rates may apply.</strong> Standard messaging and data rates from
          your wireless carrier will apply to all SMS messages you send to and receive from PlayMaker
          Pics. PlayMaker Pics does not charge any additional fees for SMS messages. Please contact
          your wireless carrier for pricing details specific to your plan.
        </Text>

        <Heading as="h3" size="md" mb={2}>2.4 Opt-Out Instructions</Heading>
        <Text mb={3}>
          You may opt out of the PlayMaker Pics SMS program at any time:
        </Text>
        <UnorderedList mb={3} pl={5} spacing={2}>
          <ListItem>
            <Text>
              Text <Text as="span" fontWeight="bold" fontSize="lg">STOP</Text> to any message
              you receive from PlayMaker Pics to immediately unsubscribe from all SMS notifications.
              After texting <Text as="span" fontWeight="bold">STOP</Text>, you will receive a
              single confirmation message and no further messages will be sent.
            </Text>
          </ListItem>
          <ListItem>
            <Text>
              You may also opt out through your account settings on our website or by contacting
              our support team.
            </Text>
          </ListItem>
        </UnorderedList>

        <Heading as="h3" size="md" mb={2}>2.5 Help & Support</Heading>
        <Text mb={3}>
          If you are experiencing issues with our SMS program or have questions:
        </Text>
        <UnorderedList mb={3} pl={5} spacing={2}>
          <ListItem>
            <Text>
              Text <Text as="span" fontWeight="bold" fontSize="lg">HELP</Text> to any message
              you receive from PlayMaker Pics to receive support information, including a link
              to these Terms, our Privacy Policy, and contact details.
            </Text>
          </ListItem>
          <ListItem>
            <Text>
              Email us at{' '}
              <Link href="mailto:support@playmakerpics.com" color="blue.500">
                support@playmakerpics.com
              </Link>
            </Text>
          </ListItem>
        </UnorderedList>

        <Heading as="h3" size="md" mb={2}>2.6 Supported Carriers</Heading>
        <Text mb={3}>
          Compatible with most major U.S. carriers including AT&T, T-Mobile, Verizon, Sprint, and
          their affiliates. Carriers are not responsible for delayed or undelivered messages.
          T-Mobile is not liable for delayed or undelivered messages.
        </Text>

        <Heading as="h3" size="md" mb={2}>2.7 Consent</Heading>
        <Text mb={3}>
          By opting in to our SMS program, you consent to receive recurring automated text messages
          from or on behalf of PlayMaker Pics at the phone number you provided. Consent is not a
          condition of purchase. For full details on how consent is collected, see our{' '}
          <Link as={RouterLink} to="/opt-in-policy" color="blue.500">SMS Opt-In Policy</Link>.
        </Text>
      </Box>

      <Box>
        <Heading as="h2" size="lg" mb={4}>3. User Accounts</Heading>
        <Text mb={3}>
          To use certain features of our service, you may need to create an account. You are
          responsible for maintaining the confidentiality of your account credentials.
        </Text>
        <Text>
          You agree to provide accurate and complete information when creating an account, and to
          update your information as needed to keep it accurate and current.
        </Text>
      </Box>

      <Box>
        <Heading as="h2" size="lg" mb={4}>4. Children's Privacy (COPPA Compliance)</Heading>
        <Text mb={3}>
          We comply with the Children's Online Privacy Protection Act ("COPPA"). COPPA requires
          that we inform parents and legal guardians about how we collect, use, and disclose
          personal information from children under 13 years of age.
        </Text>
        <Text mb={3}>
          <strong>Parental Consent:</strong> We require verifiable parental consent before
          collecting personal information from children under 13. We obtain this consent during
          the account creation process when a parent registers an athlete under 13.
        </Text>
        <Text mb={3}>
          <strong>Information We Collect:</strong> When a parent provides consent, we may collect
          the child's name, age, team information, jersey number, and images of the child captured
          during sporting events.
        </Text>
        <Text mb={3}>
          <strong>How We Use Information:</strong> We use this information solely for the purpose
          of providing our services, which include:
        </Text>
        <UnorderedList mb={3} pl={5}>
          <ListItem>Creating and maintaining athlete profiles</ListItem>
          <ListItem>Organizing and sharing photos from sporting events</ListItem>
          <ListItem>Enabling parents to access and download photos of their children</ListItem>
          <ListItem>Communicating with parents about their child's account and available photos</ListItem>
        </UnorderedList>
        <Text mb={3}>
          <strong>Parental Rights:</strong> Parents or guardians of children under 13 have the
          right to:
        </Text>
        <UnorderedList mb={3} pl={5}>
          <ListItem>Review their child's personal information</ListItem>
          <ListItem>Request deletion of their child's personal information</ListItem>
          <ListItem>Refuse further collection or use of their child's information</ListItem>
          <ListItem>Revoke previously provided consent</ListItem>
        </UnorderedList>
        <Text>
          To exercise these rights, please contact us at{' '}
          <Link href="mailto:support@playmakerpics.com" color="blue.500">
            support@playmakerpics.com
          </Link>.
        </Text>
      </Box>

      <Box>
        <Heading as="h2" size="lg" mb={4}>5. Content and Intellectual Property</Heading>
        <Text mb={3}>
          Our service allows you to upload, download, and share content such as photographs. You
          retain any intellectual property rights that you hold in that content.
        </Text>
        <Text>
          By uploading content, you grant us a worldwide, non-exclusive, royalty-free license to
          use, reproduce, adapt, publish, translate and distribute your content in any existing or
          future media for the purpose of providing our services.
        </Text>
      </Box>

      <Box>
        <Heading as="h2" size="lg" mb={4}>6. Privacy</Heading>
        <Text mb={3}>
          Our{' '}
          <Link as={RouterLink} to="/privacy" color="blue.500">Privacy Policy</Link>{' '}
          explains how we collect, use, and protect your personal information, including
          information collected through our SMS program. Your personal information will not be
          shared with third parties for marketing purposes.
        </Text>
      </Box>

      <Box>
        <Heading as="h2" size="lg" mb={4}>7. Prohibited Uses</Heading>
        <Text mb={3}>You agree not to:</Text>
        <UnorderedList mb={3} pl={5}>
          <ListItem>Use the service for any unlawful purpose</ListItem>
          <ListItem>Upload content that infringes on intellectual property rights</ListItem>
          <ListItem>Attempt to gain unauthorized access to other users' accounts</ListItem>
          <ListItem>Use the service to distribute spam or unsolicited messages</ListItem>
          <ListItem>Interfere with the proper functioning of the service</ListItem>
        </UnorderedList>
      </Box>

      <Box>
        <Heading as="h2" size="lg" mb={4}>8. Limitation of Liability</Heading>
        <Text mb={3}>
          To the maximum extent permitted by law, Setora, LLC and PlayMaker Pics shall not be
          liable for any indirect, incidental, special, consequential, or punitive damages arising
          from your use of our services, including but not limited to SMS messaging services.
        </Text>
      </Box>

      <Box>
        <Heading as="h2" size="lg" mb={4}>9. Changes to Terms</Heading>
        <Text>
          We may modify these Terms at any time. We will notify you of any significant changes by
          posting the updated Terms on our website and updating the "Last Updated" date. Your
          continued use of our services after such modifications constitutes your acceptance of the
          modified Terms.
        </Text>
      </Box>

      <Divider my={4} />

      <Box>
        <Heading as="h2" size="lg" mb={4}>Contact Information</Heading>
        <Text mb={2}><strong>Setora, LLC</strong> (operating as PlayMaker Pics)</Text>
        <Text mb={2}>
          Email:{' '}
          <Link href="mailto:support@playmakerpics.com" color="blue.500">
            support@playmakerpics.com
          </Link>
        </Text>
        <Text mb={4}>
          Website:{' '}
          <Link href="https://www.playmakerpics.com" color="blue.500" isExternal>
            www.playmakerpics.com
          </Link>
        </Text>
        <VStack spacing={1} align="start">
          <Link as={RouterLink} to="/privacy" color="blue.500">Privacy Policy</Link>
          <Link as={RouterLink} to="/opt-in-policy" color="blue.500">SMS Opt-In Policy</Link>
        </VStack>
      </Box>
    </VStack>
    <Footer />
  </Container>
);

export default TermsOfService;
